// Spec schema: a declarative report definition that Johku runs server-side without an LLM.
// Sources name existing report types plus filters; nodes bind to source data by dotted path.
// No code, SQL or raw API paths. The PHP side enforces capabilities; this module lints for
// agents and emits the JSON Schema and source catalog the backend reads.
import * as s from './schema-dsl.ts'
import { INTENTS, PRESET_DESCRIPTION, presetIds } from './tokens.ts'
import { VALUE_FORMATS } from './format.ts'
import { RULE_OPS } from './rules.ts'
import { LABEL, chartSchema, commonChartProps, cartesianOptionProps, seriesStyleProps, CARTESIAN_KINDS } from './charts.ts'
import { buildNodeVariants, nodeSchema, ruleSchema, countNodes, COLUMN_KEY_RE, LOCALE_RE, MAX_DEPTH, CONTAINER_TYPES } from './document.ts'
import { lintDesign } from './design-lint.ts'
import { OPERATIONS, OPERATIONS_VERSION, RATE_LIMITS } from './operations.ts'

export const SPEC_VERSION = 1
/**
 * report: runs server-side into an artifact. dashboard: rendered live in POS for the viewer.
 * interface (interface.ts, its own schema): an interactive POS view calling named operations.
 */
export const SPEC_KINDS = ['report', 'dashboard'] as const
export const SPEC_TIERS = ['compose', 'evaluate'] as const
export type SpecTier = typeof SPEC_TIERS[number]

export const MAX_SPEC_BYTES = 200_000
export const MAX_SPEC_NODES = 500
export const MAX_SOURCES = 8
export const MAX_RECIPIENTS = 10

export const CAPABILITIES = [
  'read:sales', 'read:payments', 'read:reservations', 'read:customers', 'deliver:email',
  'read:products', 'read:issues', 'write:products', 'write:stock', 'write:issues',
  'read:orders', 'write:orders', 'read:bills',
  'read:resources', 'write:resources',
  'read:categories', 'write:categories',
  'read:tags', 'write:tags',
  'read:discounts', 'write:discounts', 'read:shows', 'read:forms', 'read:pricetiers',
  'read:collections', 'read:groups', 'read:content', 'write:content', 'read:messages', 'write:messages',
  'read:users', 'read:poses',
  'write:customers', 'write:reservations', 'read:campaigns',
  'read:settings', 'read:files', 'read:deliverymethods',
  'read:metadata', 'write:metadata'
] as const
export type Capability = typeof CAPABILITIES[number]
export type Risk = 'read' | 'egress' | 'write'

/** Minimum user level for each capability. Mirrors Security::CLIENT (3) and SITEADMIN (7). */
export const CAPABILITY_LEVELS: Record<Capability, number> = {
  'read:sales': 3,
  'read:payments': 3,
  'read:reservations': 3,
  'read:customers': 7,
  'deliver:email': 7,
  'read:products': 3,
  'read:issues': 3,
  'write:products': 3,
  'write:stock': 3,
  'write:issues': 3,
  'read:orders': 3,
  'write:orders': 3,
  'read:bills': 3,
  'read:resources': 3,
  'write:resources': 3,
  'read:categories': 3,
  'write:categories': 3,
  'read:tags': 3,
  'write:tags': 3,
  'read:discounts': 3,
  'write:discounts': 3,
  'read:shows': 3,
  'read:forms': 3,
  'read:pricetiers': 3,
  'read:collections': 3,
  'read:groups': 3,
  'read:content': 3,
  'write:content': 3,
  'read:messages': 3,
  'write:messages': 3,
  'read:users': 7,
  'read:poses': 3,
  'write:customers': 7,
  'write:reservations': 3,
  'read:campaigns': 7,
  'read:settings': 3,
  'read:files': 3,
  'read:deliverymethods': 3,
  'read:metadata': 3,
  'write:metadata': 3
}

export const CAPABILITY_RISK: Record<Capability, Risk> = {
  'read:sales': 'read',
  'read:payments': 'read',
  'read:reservations': 'read',
  'read:customers': 'read',
  'deliver:email': 'egress',
  'read:products': 'read',
  'read:issues': 'read',
  'write:products': 'write',
  'write:stock': 'write',
  'write:issues': 'write',
  'read:orders': 'read',
  'write:orders': 'write',
  'read:bills': 'read',
  'read:resources': 'read',
  'write:resources': 'write',
  'read:categories': 'read',
  'write:categories': 'write',
  'read:tags': 'read',
  'write:tags': 'write',
  'read:discounts': 'read',
  'write:discounts': 'write',
  'read:shows': 'read',
  'read:forms': 'read',
  'read:pricetiers': 'read',
  'read:collections': 'read',
  'read:groups': 'read',
  'read:content': 'read',
  'write:content': 'write',
  'read:messages': 'read',
  'write:messages': 'write',
  'read:users': 'read',
  'read:poses': 'read',
  'write:customers': 'write',
  'write:reservations': 'write',
  'read:campaigns': 'read',
  'read:settings': 'read',
  'read:files': 'read',
  'read:deliverymethods': 'read',
  'read:metadata': 'read',
  'write:metadata': 'write'
}

export const PERIODS = [
  'today', 'yesterday', 'last_7_days', 'last_30_days', 'this_week', 'last_week',
  'this_month', 'last_month', 'this_year', 'last_year', 'all_time'
] as const
export type Period = typeof PERIODS[number]

export const FILTER_KEYS = [
  'products', 'categories', 'producttypes', 'resources', 'saleschannels', 'poses', 'deliverymethods',
  'followup_products', 'limit', 'sort', 'min_spend'
] as const
export type FilterKey = typeof FILTER_KEYS[number]

export interface ReportSourceDef {
  capability: Capability
  /** Output identifies people. Artifacts get an expiry and are covered by GDPR erasure. */
  personal: boolean
  filters: FilterKey[]
  requires?: FilterKey[]
}

const CATALOG_FILTERS: FilterKey[] = ['products', 'categories', 'producttypes', 'resources', 'saleschannels', 'poses', 'deliverymethods']

/** Curated subset of the POS Create-report types. Accounting dumps stay out on purpose. */
export const REPORT_SOURCES: Record<string, ReportSourceDef> = {
  sales: { capability: 'read:sales', personal: false, filters: CATALOG_FILTERS },
  groupedsales: { capability: 'read:sales', personal: false, filters: CATALOG_FILTERS },
  channelsales: { capability: 'read:sales', personal: false, filters: CATALOG_FILTERS },
  discount: { capability: 'read:sales', personal: false, filters: CATALOG_FILTERS },
  voucher: { capability: 'read:sales', personal: false, filters: CATALOG_FILTERS },
  payments: { capability: 'read:payments', personal: false, filters: CATALOG_FILTERS },
  advances: { capability: 'read:payments', personal: false, filters: CATALOG_FILTERS },
  participants: { capability: 'read:reservations', personal: false, filters: CATALOG_FILTERS },
  monthlyreservations: { capability: 'read:reservations', personal: false, filters: CATALOG_FILTERS },
  weeklyreservations: { capability: 'read:reservations', personal: false, filters: CATALOG_FILTERS },
  dailyreservations: { capability: 'read:reservations', personal: false, filters: CATALOG_FILTERS },
  hourlyreservations: { capability: 'read:reservations', personal: false, filters: CATALOG_FILTERS },
  productoccupancy: { capability: 'read:reservations', personal: false, filters: CATALOG_FILTERS },
  resourceoccupancy: { capability: 'read:reservations', personal: false, filters: CATALOG_FILTERS },
  customerltv: { capability: 'read:customers', personal: true, filters: ['limit', 'sort', 'min_spend'] },
  customercohort: { capability: 'read:customers', personal: true, filters: ['products', 'followup_products', 'limit'], requires: ['products'] },
  /** Current stock, not a period: one row per stocked product */
  stock: { capability: 'read:products', personal: false, filters: ['products', 'categories', 'resources'] }
}

export interface LiveSourceDef {
  /** reports/{endpoint}.json, the cheap synchronous queries behind the POS dashboard widgets */
  endpoint: string
  capability: Capability
  personal: boolean
  limit: boolean
  /** Row fields, for authors writing binding paths */
  fields: string[]
}

/**
 * Dashboard-only sources, fetched by POS with the viewer's own credentials and cached briefly.
 * Output is { rows, count, totals } where totals sums the numeric total/vat/count fields.
 */
export const LIVE_SOURCES: Record<string, LiveSourceDef> = {
  monthly_sales: { endpoint: 'sales', capability: 'read:sales', personal: false, limit: false, fields: ['y', 'm', 'total', 'vat'] },
  cumulative_sales: { endpoint: 'cumulativesales', capability: 'read:sales', personal: false, limit: false, fields: ['y', 'm', 'total', 'vat'] },
  top_products: { endpoint: 'bestsellingproducts', capability: 'read:sales', personal: false, limit: true, fields: ['id', 'name', 'total', 'vat'] },
  top_reservable_products: { endpoint: 'mostreservedproducts', capability: 'read:reservations', personal: false, limit: true, fields: ['id', 'name', 'total', 'vat'] },
  top_sales_channels: { endpoint: 'highestsaleschannels', capability: 'read:sales', personal: false, limit: true, fields: ['origin', 'count', 'total', 'vat'] },
  top_customers: { endpoint: 'bestcustomers', capability: 'read:customers', personal: true, limit: true, fields: ['id', 'name', 'company', 'count', 'total', 'vat'] }
}

export const DEFAULT_LIVE_MAX_AGE = 5
export const DEFAULT_SNAPSHOT_MAX_AGE = 360

// ---------------------------------------------------------------------------
// Types

export interface Binding { source: string, path: string }
export interface RowFilter { field: string, op: typeof RULE_OPS[number], value?: unknown }
export interface RowsBinding extends Binding { where?: RowFilter[], sort?: { key: string, dir?: 'asc' | 'desc' }, limit?: number }
export interface Condition extends Binding { op: typeof RULE_OPS[number], value?: unknown }
export interface SpecSource {
  report?: string
  live?: string
  period?: Period
  filters?: Partial<Record<FilterKey, unknown>>
  limit?: number
  refresh?: { maxAgeMinutes: number }
}
export interface SpecSchedule { weekdays?: number[], days?: number[], months?: number[], times: string[] }

export interface MaxSpec {
  version: 1
  kind: typeof SPEC_KINDS[number]
  tier: SpecTier
  title: string
  description?: string
  locale: string
  preset?: string
  capabilities: Capability[]
  period?: Period
  sources: Record<string, SpecSource>
  body: Record<string, unknown>[]
  schedule?: SpecSchedule
  delivery?: { email?: { recipients: number[], attach?: EmailAttachment, onlyIfRows?: boolean } }
}

export const EMAIL_ATTACHMENTS = ['pdf'] as const
export type EmailAttachment = typeof EMAIL_ATTACHMENTS[number]

// ---------------------------------------------------------------------------
// Schema

export const SOURCE_ID_RE = /^[a-z][a-z0-9_]{0,31}$/
// Dotted path into report output: identifiers or list indexes, at most eight segments.
export const PATH_RE = /^([A-Za-z_][A-Za-z0-9_]{0,63}|[0-9]{1,6})(\.([A-Za-z_][A-Za-z0-9_]{0,63}|[0-9]{1,6})){0,7}$/
const TIME_RE = /^([01][0-9]|2[0-3]):[0-5][0-9]$/

const intent = s.enumOf(INTENTS)
const valueFormat = s.enumOf(VALUE_FORMATS)
const currencyCode = s.string({ min: 3, max: 3, pattern: /^[A-Z]{3}$/ })
const scalar = s.anyOf([s.string({ max: 500 }), s.number(), s.boolean(), s.nullValue()])
const sourceId = s.string({ max: 32, pattern: SOURCE_ID_RE, description: 'Key in spec.sources' })
const dataPath = s.string({ max: 300, pattern: PATH_RE, description: 'Dotted path into the report output, e.g. salestotal.total or sales' })
const fieldPath = s.string({ max: 300, pattern: PATH_RE, description: 'Dotted path inside one row, e.g. total' })
const ids = (description: string) => s.array(s.number({ min: 1, integer: true }), { max: 200, description })

const binding = s.object({ source: sourceId, path: dataPath }, {
  required: ['source', 'path'],
  description: 'Reads one value from a source when the spec runs'
})

const rowFilter = s.object({
  field: fieldPath,
  op: s.enumOf(RULE_OPS),
  value: s.anyOf([scalar, s.array(scalar, { max: 50 })])
}, {
  required: ['field', 'op'],
  refine (v, _p, report) {
    const needsValue = !['empty', 'notEmpty'].includes(v.op as string)
    if (needsValue && v.value === undefined) report('value', 'is required for this op')
    if (['in', 'notIn'].includes(v.op as string) && !Array.isArray(v.value)) report('value', 'must be an array for in/notIn')
    if (v.op === 'between' && (!Array.isArray(v.value) || v.value.length !== 2)) report('value', 'must be [low, high] for between')
  }
})

const rowsBinding = s.object({
  source: sourceId,
  path: dataPath,
  where: s.array(rowFilter, { min: 1, max: 10, description: 'evaluate tier only: keeps the rows that match every filter, before sort and limit; delivery.email.onlyIfRows counts the rows kept' }),
  sort: s.object({ key: fieldPath, dir: s.enumOf(['asc', 'desc']) }, { required: ['key'] }),
  limit: s.number({ min: 1, max: 1000, integer: true })
}, { required: ['source', 'path'], description: 'Reads a list of rows from a source when the spec runs' })

const seriesBinding = s.object({
  source: sourceId,
  path: dataPath,
  field: fieldPath,
  limit: s.number({ min: 1, max: 120, integer: true })
}, { required: ['source', 'path'] })

export const conditionSchema = s.object({
  source: sourceId,
  path: dataPath,
  op: s.enumOf(RULE_OPS),
  value: s.anyOf([scalar, s.array(scalar, { max: 50 })])
}, {
  required: ['source', 'path', 'op'],
  description: 'evaluate tier only: the node is left out unless the condition holds'
})

const filtersSchema = s.object({
  products: ids('Product ids'),
  categories: ids('Category ids'),
  producttypes: s.array(s.string({ max: 40, pattern: /^[0-9]{1,4}-[0-9]{1,4}-[A-Za-z0-9_]{0,32}$/ }), { max: 50 }),
  resources: ids('Resource ids'),
  saleschannels: s.array(s.string({ max: 64, pattern: /^[A-Za-z0-9_.:-]{1,64}$/ }), { max: 50 }),
  poses: s.array(s.number({ min: 0, integer: true }), { max: 50 }),
  deliverymethods: ids('Delivery method ids'),
  followup_products: ids('Follow-up product ids for customercohort'),
  limit: s.number({ min: 1, max: 200, integer: true }),
  sort: s.enumOf(['total', 'name', 'first_purchase', 'last_purchase', 'bill_count']),
  min_spend: s.number({ min: 0 })
})

const sourceSchema = s.object({
  report: s.enumOf(Object.keys(REPORT_SOURCES), 'Report run by the job queue; in dashboards its output is kept as a snapshot'),
  live: s.enumOf(Object.keys(LIVE_SOURCES), 'Dashboards only: quick query fetched when the dashboard is viewed'),
  period: s.enumOf(PERIODS, 'Overrides spec.period for this source'),
  filters: filtersSchema,
  limit: s.number({ min: 1, max: 50, integer: true, description: 'Rows for live top-N sources' }),
  refresh: s.object({
    maxAgeMinutes: s.number({ min: 1, max: 1440, integer: true })
  }, { required: ['maxAgeMinutes'], description: `Dashboards only: how old the data may be before it is fetched again (live default ${DEFAULT_LIVE_MAX_AGE}, snapshot default ${DEFAULT_SNAPSHOT_MAX_AGE})` })
}, {
  description: 'Exactly one of report or live',
  refine: (v, _p, report) => {
    const hasReport = v.report !== undefined
    const hasLive = v.live !== undefined
    if (hasReport === hasLive) report('', 'needs exactly one of report or live')
    if (hasLive && v.filters !== undefined) report('filters', 'live sources take no filters')
    if (hasReport && v.limit !== undefined) report('limit', 'report sources set limit in filters')
    if (hasLive && v.limit !== undefined && !LIVE_SOURCES[v.live as string]?.limit) report('limit', `is not supported by ${String(v.live)}`)
  }
})

const scheduleSchema = s.object({
  weekdays: s.array(s.number({ min: 1, max: 7, integer: true }), { max: 7, description: '1 = Monday' }),
  days: s.array(s.number({ min: 1, max: 31, integer: true }), { max: 31 }),
  months: s.array(s.number({ min: 1, max: 12, integer: true }), { max: 12 }),
  times: s.array(s.string({ max: 5, pattern: TIME_RE }), { min: 1, max: 4, description: 'Shop-local HH:MM; Johku shifts each by a few minutes per shop' })
}, { required: ['times'] })

const deliverySchema = s.object({
  email: s.object({
    recipients: s.array(s.number({ min: 1, integer: true }), { min: 1, max: MAX_RECIPIENTS, description: 'Verified shop user ids. The email only links to the report in POS unless attach is set.' }),
    attach: s.enumOf(EMAIL_ATTACHMENTS, 'Opt-in: also attach the report as a PDF. The copy leaves Johku and cannot be recalled, so specs with personal data cannot attach.'),
    onlyIfRows: s.boolean('Send only when a bound table or chart has at least one row; the report is stored either way')
  }, { required: ['recipients'] })
})

function boundCartesian (kind: string) {
  return s.object({
    kind: s.literal(kind),
    data: rowsBinding,
    category: fieldPath,
    series: s.array(s.object({ field: fieldPath, name: LABEL, ...seriesStyleProps }, { required: ['field', 'name'] }), { min: 1, max: 12 }),
    ...cartesianOptionProps
  }, { required: ['kind', 'data', 'category', 'series'] })
}

/** Chart kinds that take { label, value } items and can be bound to rows the same way */
export const BOUND_ITEM_KINDS = ['donut', 'funnel', 'treemap'] as const

function boundItems (kind: string) {
  return s.object({
    kind: s.literal(kind),
    data: rowsBinding,
    label: fieldPath,
    value: fieldPath,
    ...(kind === 'donut' ? { centerLabel: s.string({ max: 40 }) } : {}),
    ...(kind === 'funnel' ? { sort: s.enumOf(['descending', 'none']) } : {}),
    ...commonChartProps
  }, { required: ['kind', 'data', 'label', 'value'] })
}

function boundChartSchema () {
  const variants: Record<string, s.Schema> = {}
  for (const kind of BOUND_ITEM_KINDS) variants[kind] = boundItems(kind)
  for (const kind of CARTESIAN_KINDS) variants[kind] = boundCartesian(kind)
  return s.oneOfBy('kind', variants, 'Chart built from source rows when the spec runs')
}

function specNodeVariants (): Record<string, s.Schema> {
  const extra = { when: conditionSchema }
  const node = (type: string, props: Record<string, s.Schema>, required: string[] = [], refine?: s.ObjectOpts['refine']) =>
    nodeSchema(type, props, required, refine, extra)
  return {
    ...buildNodeVariants(extra),
    metric: node('metric', {
      label: s.string({ max: 80 }),
      value: s.anyOf([s.number(), s.string({ max: 40 }), s.nullValue(), binding]),
      format: valueFormat,
      currency: s.anyOf([currencyCode, binding]),
      delta: s.object({
        value: s.anyOf([s.number(), binding]),
        format: valueFormat,
        goodDirection: s.enumOf(['up', 'down']),
        label: s.string({ max: 60 })
      }, { required: ['value'] }),
      intent,
      caption: s.string({ max: 160 }),
      rules: s.array(ruleSchema, { max: 10 }),
      sparkline: s.anyOf([s.array(s.anyOf([s.number(), s.nullValue()]), { max: 120 }), seriesBinding])
    }, ['label', 'value']),
    table: node('table', {
      title: s.string({ max: 120 }),
      columns: s.array(s.object({
        key: s.string({ max: 64, pattern: COLUMN_KEY_RE }),
        label: s.string({ max: 80 }),
        field: fieldPath,
        format: valueFormat,
        currency: s.anyOf([currencyCode, binding]),
        align: s.enumOf(['start', 'center', 'end']),
        width: s.enumOf(['auto', 'narrow', 'wide'])
      }, { required: ['key', 'label'] }), { min: 1, max: 24 }),
      rows: s.anyOf([s.array(s.record(scalar, { maxKeys: 24, keyPattern: COLUMN_KEY_RE }), { max: 2000 }), rowsBinding]),
      rules: s.array(ruleSchema, { max: 20 }),
      caption: s.string({ max: 300 }),
      maxRows: s.number({ min: 1, max: 500, integer: true })
    }, ['columns', 'rows'], (v, _p, report) => {
      const keys = new Set((v.columns as { key: string }[]).map(c => c.key))
      if (keys.size !== (v.columns as unknown[]).length) report('columns', 'column keys must be unique')
      ;((v.rules as { field: string }[] | undefined) ?? []).forEach((r, i) => {
        if (!keys.has(r.field)) report(`rules[${i}].field`, 'is not a column key')
      })
    }),
    chart: node('chart', {
      chart: s.anyOf([chartSchema(), boundChartSchema()]),
      title: s.string({ max: 120 }),
      caption: s.string({ max: 300 }),
      height: s.enumOf(['sm', 'md', 'lg'])
    }, ['chart'])
  }
}

let cached: s.Validator | null = null

export function specValidator (): s.Validator {
  if (cached) return cached
  const root = s.object({
    version: s.literal(SPEC_VERSION),
    kind: s.enumOf(SPEC_KINDS),
    tier: s.enumOf(SPEC_TIERS, 'compose: report types and filters; evaluate: adds rules and conditions'),
    title: s.string({ min: 1, max: 160 }),
    description: s.string({ max: 600 }),
    locale: s.string({ max: 5, pattern: LOCALE_RE, description: 'Language of the titles and text, e.g. fi_FI' }),
    preset: s.enumOf(presetIds(), PRESET_DESCRIPTION),
    capabilities: s.array(s.enumOf(CAPABILITIES), { max: CAPABILITIES.length, description: 'Everything the spec needs; checked on save and again on every run' }),
    period: s.enumOf(PERIODS, 'Default period for every source'),
    sources: s.record(sourceSchema, { maxKeys: MAX_SOURCES, keyPattern: SOURCE_ID_RE }),
    body: s.array(s.ref('Node'), { min: 1, max: 200 }),
    schedule: scheduleSchema,
    delivery: deliverySchema
  }, { required: ['version', 'kind', 'tier', 'title', 'locale', 'capabilities', 'sources', 'body'] })
  cached = { root, defs: { Node: s.oneOfBy('type', specNodeVariants(), 'Spec node') }, maxDepth: MAX_DEPTH }
  return cached
}

export function specJsonSchema (): Record<string, unknown> {
  return {
    ...s.toJsonSchema(specValidator(), { id: 'https://schemas.johku.com/max/spec.v1.json', title: 'Johku Max spec' }),
    'x-max-bytes': MAX_SPEC_BYTES,
    'x-max-nodes': MAX_SPEC_NODES
  }
}

/** Catalog the backend reads so capability levels, risk and personal-data flags have one source. */
export function specCatalog (): Record<string, unknown> {
  return {
    version: SPEC_VERSION,
    capabilities: Object.fromEntries(CAPABILITIES.map(c => [c, { level: CAPABILITY_LEVELS[c], risk: CAPABILITY_RISK[c] }])),
    sources: REPORT_SOURCES,
    liveSources: LIVE_SOURCES,
    refreshDefaults: { live: DEFAULT_LIVE_MAX_AGE, snapshot: DEFAULT_SNAPSHOT_MAX_AGE },
    periods: PERIODS,
    maxNodes: MAX_SPEC_NODES,
    maxRecipients: MAX_RECIPIENTS,
    operationsVersion: OPERATIONS_VERSION,
    rateLimits: RATE_LIMITS,
    operations: OPERATIONS
  }
}

/** Spec kinds with their own schema register here, so lintSpec and describeSpec accept them. */
export interface SpecKindHandler {
  lint (spec: unknown): SpecLintResult
  analyze (spec: unknown): SpecAnalysis
  describe (spec: unknown): SpecDescription
}

const KIND_HANDLERS: Record<string, SpecKindHandler> = {}

export function registerSpecKind (kind: string, handler: SpecKindHandler): void {
  KIND_HANDLERS[kind] = handler
}

function kindHandler (spec: unknown): SpecKindHandler | undefined {
  const kind = spec && typeof spec === 'object' ? (spec as { kind?: unknown }).kind : undefined
  return typeof kind === 'string' && Object.prototype.hasOwnProperty.call(KIND_HANDLERS, kind) ? KIND_HANDLERS[kind] : undefined
}

// ---------------------------------------------------------------------------
// Lint

export interface SpecAnalysis {
  requiredCapabilities: Capability[]
  risk: Risk
  personalData: boolean
  minLevel: number
  /** Level needed to see the result: the highest read capability, ignoring delivery */
  viewLevel: number
  bindings: Binding[]
  usesRules: boolean
  usesConditions: boolean
}

export interface SpecLintResult extends s.ValidationResult {
  warnings: s.Issue[]
  analysis: SpecAnalysis | null
}

function walkSpecNodes (nodes: unknown, visit: (node: Record<string, unknown>, path: string) => void, path = 'body', depth = 1): void {
  if (!Array.isArray(nodes) || depth > MAX_DEPTH) return
  nodes.forEach((node, i) => {
    if (!node || typeof node !== 'object') return
    const p = `${path}[${i}]`
    visit(node as Record<string, unknown>, p)
    const type = (node as { type?: string }).type
    if ((CONTAINER_TYPES as readonly string[]).includes(type ?? '')) {
      walkSpecNodes((node as { children?: unknown }).children, visit, `${p}.children`, depth + 1)
    }
  })
}

function isBinding (v: unknown): v is Binding {
  return !!v && typeof v === 'object' && !Array.isArray(v) && typeof (v as Binding).source === 'string' && typeof (v as Binding).path === 'string'
}

/** Bindings that read a list of rows (table rows, chart data); delivery.email.onlyIfRows checks these */
const ROW_BINDING_RE = /\.(rows|chart\.data)$/

/** A table or chart whose bound rows are filtered with where, which needs tier evaluate like when */
function filtersRows (node: Record<string, unknown>): boolean {
  const data = node.type === 'table' ? node.rows : node.type === 'chart' ? (node.chart as { data?: unknown } | undefined)?.data : undefined
  return isBinding(data) && Array.isArray((data as RowsBinding).where) && (data as RowsBinding).where!.length > 0
}

export function collectBindings (spec: MaxSpec): { binding: Binding, path: string }[] {
  const out: { binding: Binding, path: string }[] = []
  const add = (v: unknown, path: string) => { if (isBinding(v)) out.push({ binding: v, path }) }
  walkSpecNodes(spec.body, (node, p) => {
    add(node.when, `${p}.when`)
    if (node.type === 'metric') {
      add(node.value, `${p}.value`)
      add(node.currency, `${p}.currency`)
      add((node.delta as { value?: unknown } | undefined)?.value, `${p}.delta.value`)
      add(node.sparkline, `${p}.sparkline`)
    }
    if (node.type === 'table') {
      add(node.rows, `${p}.rows`)
      ;((node.columns as { currency?: unknown }[] | undefined) ?? []).forEach((c, i) => add(c.currency, `${p}.columns[${i}].currency`))
    }
    if (node.type === 'chart') add((node.chart as { data?: unknown } | undefined)?.data, `${p}.chart.data`)
  })
  return out
}

export function sourceDef (source: SpecSource): ReportSourceDef | LiveSourceDef | undefined {
  if (source.live !== undefined) return LIVE_SOURCES[source.live]
  return source.report !== undefined ? REPORT_SOURCES[source.report] : undefined
}

export function analyzeSpec (spec: MaxSpec): SpecAnalysis {
  const handler = kindHandler(spec)
  if (handler) return handler.analyze(spec)
  const required = new Set<Capability>()
  let personalData = false
  for (const source of Object.values(spec.sources ?? {})) {
    const def = sourceDef(source)
    if (!def) continue
    required.add(def.capability)
    if (def.personal) personalData = true
  }
  if (spec.delivery?.email) required.add('deliver:email')
  let usesRules = false
  let usesConditions = false
  walkSpecNodes(spec.body, node => {
    if (node.when !== undefined || filtersRows(node)) usesConditions = true
    if (Array.isArray(node.rules) && node.rules.length) usesRules = true
  })
  const requiredCapabilities = CAPABILITIES.filter(c => required.has(c))
  const risks = requiredCapabilities.map(c => CAPABILITY_RISK[c])
  const risk: Risk = risks.includes('write') ? 'write' : risks.includes('egress') ? 'egress' : 'read'
  const minLevel = requiredCapabilities.reduce((lvl, c) => Math.max(lvl, CAPABILITY_LEVELS[c]), 3)
  const viewLevel = requiredCapabilities.filter(c => CAPABILITY_RISK[c] === 'read').reduce((lvl, c) => Math.max(lvl, CAPABILITY_LEVELS[c]), 3)
  return { requiredCapabilities, risk, personalData, minLevel, viewLevel, bindings: collectBindings(spec).map(b => b.binding), usesRules, usesConditions }
}

// ---------------------------------------------------------------------------
// Plain-language description for the approval screen. Returns facts, not prose, so the host
// renders them with its own translations and the approver never has to read JSON.

export interface SpecFilterFact { key: FilterKey, ids?: number[], value?: string | number }
export interface SpecSourceFact {
  id: string
  report: string | null
  live: string | null
  capability: Capability
  personal: boolean
  period: Period
  filters: SpecFilterFact[]
  limit: number | null
  maxAgeMinutes: number | null
}
export interface SpecOutlineItem { type: string, label: string | null, sources: string[], depth: number, conditional: boolean, rules: number }
export interface SpecDescription {
  title: string
  description: string | null
  tier: SpecTier
  risk: Risk
  personalData: boolean
  approvalLevel: number
  capabilities: { id: Capability, risk: Risk, level: number }[]
  sources: SpecSourceFact[]
  schedule: Required<SpecSchedule> | null
  recipients: number[]
  /** Set when the email carries a copy of the report instead of only a link */
  attachment: EmailAttachment | null
  outline: SpecOutlineItem[]
  outlineTruncated: boolean
  /** Interfaces only: the named operations, each shown to the approver with its catalog meaning */
  operations?: SpecOperationFact[]
  state?: string[]
  metadataNamespaces?: string[]
  /** Interfaces: declared or all; all reads every metadata namespace */
  metadataRead?: 'declared' | 'all'
}
export interface SpecOperationFact {
  name: string
  operation: string
  mode: 'read' | 'write'
  capability: Capability
  label: string | null
  /** Places in the interface that call it, e.g. a button label */
  calledFrom: string[]
  /** Writes: once approved, calls changing more items than this ask first; null saves without asking */
  confirmAbove?: number | null
}

const OUTLINE_MAX = 100

function nodeLabel (node: Record<string, unknown>): string | null {
  const pick = (v: unknown) => typeof v === 'string' && v.trim() ? v.trim().slice(0, 120) : null
  switch (node.type) {
    case 'heading': case 'text': return pick(node.text)
    case 'metric': return pick(node.label)
    case 'link': return pick(node.label) ?? pick(node.text)
    case 'chart': return pick((node.chart as { title?: unknown } | undefined)?.title) ?? pick(node.title)
    default: return pick(node.title) ?? pick(node.label)
  }
}

/** Minutes a dashboard source's data may be reused before it is fetched or run again */
export function sourceMaxAge (source: SpecSource): number {
  return source.refresh?.maxAgeMinutes ?? (source.live !== undefined ? DEFAULT_LIVE_MAX_AGE : DEFAULT_SNAPSHOT_MAX_AGE)
}

export function describeSpec (spec: MaxSpec): SpecDescription {
  const handler = kindHandler(spec)
  if (handler) return handler.describe(spec)
  const analysis = analyzeSpec(spec)
  const sources: SpecSourceFact[] = Object.entries(spec.sources ?? {}).map(([id, source]) => {
    const def = sourceDef(source)
    const filters: SpecFilterFact[] = Object.entries(source.filters ?? {}).map(([key, value]) =>
      Array.isArray(value) ? { key: key as FilterKey, ids: value as number[] } : { key: key as FilterKey, value: value as string | number })
    return {
      id,
      report: source.report ?? null,
      live: source.live ?? null,
      capability: def?.capability as Capability,
      personal: !!def?.personal,
      period: source.period ?? spec.period ?? 'yesterday',
      filters,
      limit: source.limit ?? null,
      maxAgeMinutes: spec.kind === 'dashboard' ? sourceMaxAge(source) : null
    }
  })

  const bindingsByNode = new Map<string, Set<string>>()
  for (const { binding, path } of collectBindings(spec)) {
    const nodePath = path.replace(/\.(when|value|currency|delta\.value|sparkline|rows|columns\[\d+\]\.currency|chart\.data)$/, '')
    if (!bindingsByNode.has(nodePath)) bindingsByNode.set(nodePath, new Set())
    bindingsByNode.get(nodePath)!.add(binding.source)
  }
  const outline: SpecOutlineItem[] = []
  let total = 0
  walkSpecNodes(spec.body, (node, path) => {
    total++
    if (outline.length >= OUTLINE_MAX) return
    outline.push({
      type: String(node.type),
      label: nodeLabel(node),
      sources: [...(bindingsByNode.get(path) ?? [])],
      depth: (path.match(/\.children\[/g) ?? []).length,
      conditional: node.when !== undefined,
      rules: Array.isArray(node.rules) ? node.rules.length : 0
    })
  })

  return {
    title: spec.title,
    description: spec.description ?? null,
    tier: spec.tier,
    risk: analysis.risk,
    personalData: analysis.personalData,
    approvalLevel: analysis.risk === 'read' ? analysis.minLevel : Math.max(analysis.minLevel, 7),
    capabilities: analysis.requiredCapabilities.map(id => ({ id, risk: CAPABILITY_RISK[id], level: CAPABILITY_LEVELS[id] })),
    sources,
    schedule: spec.schedule
      ? { weekdays: spec.schedule.weekdays ?? [], days: spec.schedule.days ?? [], months: spec.schedule.months ?? [], times: spec.schedule.times }
      : null,
    recipients: spec.delivery?.email?.recipients ?? [],
    attachment: spec.delivery?.email?.attach ?? null,
    outline,
    outlineTruncated: total > outline.length
  }
}

export function lintSpec (spec: unknown): SpecLintResult {
  const bytes = new TextEncoder().encode(JSON.stringify(spec ?? null)).length
  if (bytes > MAX_SPEC_BYTES) {
    return { valid: false, issues: [{ path: '$', message: `spec is ${bytes} bytes; the limit is ${MAX_SPEC_BYTES}` }], warnings: [], analysis: null }
  }
  const handler = kindHandler(spec)
  if (handler) return handler.lint(spec)
  const result = s.validateWith(specValidator(), spec)
  if (!result.valid) return { ...result, warnings: [], analysis: null }
  const doc = spec as MaxSpec
  const issues: s.Issue[] = []
  const warnings: s.Issue[] = []
  const analysis = analyzeSpec(doc)

  const nodes = countNodes(doc.body)
  if (nodes > MAX_SPEC_NODES) issues.push({ path: 'body', message: `spec has ${nodes} nodes; the limit is ${MAX_SPEC_NODES}` })

  for (const { binding, path } of collectBindings(doc)) {
    if (!doc.sources[binding.source]) issues.push({ path, message: `source "${binding.source}" is not declared in sources` })
  }

  if (doc.tier === 'compose') {
    if (analysis.usesConditions) issues.push({ path: 'tier', message: 'when conditions and row filters need tier "evaluate"' })
    if (analysis.usesRules) issues.push({ path: 'tier', message: 'rules need tier "evaluate"' })
  }

  const declared = new Set(doc.capabilities)
  for (const cap of analysis.requiredCapabilities) {
    if (!declared.has(cap)) issues.push({ path: 'capabilities', message: `missing "${cap}", which the spec needs` })
  }
  for (const cap of declared) {
    if (!analysis.requiredCapabilities.includes(cap)) warnings.push({ path: 'capabilities', message: `"${cap}" is declared but not used` })
  }

  const dashboard = doc.kind === 'dashboard'
  for (const [id, source] of Object.entries(doc.sources)) {
    if (source.live !== undefined) {
      if (!dashboard) issues.push({ path: `sources.${id}.live`, message: 'live sources are only available in dashboards; use a report source' })
      continue
    }
    if (source.refresh && !dashboard) warnings.push({ path: `sources.${id}.refresh`, message: 'refresh is only used by dashboards' })
    const def = REPORT_SOURCES[source.report as string]
    if (!def) continue
    for (const key of Object.keys(source.filters ?? {})) {
      if (!def.filters.includes(key as FilterKey)) issues.push({ path: `sources.${id}.filters.${key}`, message: `is not supported by the ${source.report} report` })
    }
    for (const key of def.requires ?? []) {
      const value = source.filters?.[key]
      if (!Array.isArray(value) || !value.length) issues.push({ path: `sources.${id}.filters.${key}`, message: `is required by the ${source.report} report` })
    }
  }

  if (dashboard && doc.delivery) issues.push({ path: 'delivery', message: 'dashboards are viewed in POS and cannot send email' })
  if (doc.delivery?.email?.attach && analysis.personalData) {
    issues.push({ path: 'delivery.email.attach', message: 'specs with personal data cannot attach the report; recipients open it in POS' })
  }
  if (doc.delivery?.email?.attach) warnings.push({ path: 'delivery.email.attach', message: 'the PDF is attached only when the export service is available; otherwise the email only links to POS' })
  if (doc.delivery?.email?.onlyIfRows && !collectBindings(doc).some(b => ROW_BINDING_RE.test(b.path))) {
    warnings.push({ path: 'delivery.email.onlyIfRows', message: 'no table rows or chart data are bound, so there are no rows to check and the email always goes out' })
  }
  if (dashboard && doc.schedule && !Object.values(doc.sources).some(s => s.report !== undefined)) {
    warnings.push({ path: 'schedule', message: 'the schedule only refreshes report snapshots, and this dashboard has none' })
  }
  if (!Object.keys(doc.sources).length) warnings.push({ path: 'sources', message: 'no sources; every run produces the same document' })
  if (doc.delivery && !doc.schedule) warnings.push({ path: 'delivery', message: 'delivery only happens on scheduled runs' })
  if (analysis.risk !== 'read') warnings.push({ path: 'capabilities', message: `risk is "${analysis.risk}": a shop admin (level 7) must approve the spec in POS before it runs` })
  for (const w of lintDesign(doc.body, { preset: doc.preset })) warnings.push({ path: w.path, message: `${w.message} (design: ${w.rule})` })

  return { valid: issues.length === 0, issues, warnings, analysis }
}
